// src/components/IconButton.tsx
import React, { useState } from "react";
import type { IconConfig } from "../Header.types";
import Tooltip from "@/design-system/Atoms/Tooltip";
import { LoadingSpinner } from "@/design-system/Atoms/Spinner";
import { useAuthService } from "@/rtkQuery/features/hooks";
import { useCartService } from "@/rtkQuery/features/hooks/useCartService";

interface IconButtonProps {
  config: IconConfig;
  className?: string;
}

const IconButton: React.FC<IconButtonProps> = ({
  config,
  className = "me-1 lg:me-2",
}) => {
  const { isAuthenticated } = useAuthService();
  const { cartIsLoading = true } = useCartService() || {};
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleShowTooltip = (event: React.MouseEvent | React.FocusEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
    setIsHovered(true);
  };

  const handleHideTooltip = () => {
    setIsHovered(false);
    setTooltipPos(null);
  };

  const shouldShowCartLoading = config.id === "cart" &&
    (config.isBadgeCountLoading || (isAuthenticated && cartIsLoading));

  const iconContent = config.iconClassName ? (
    <i
      className={`${config.iconClassName} text-xl text-white`}
      aria-hidden="true"
    />
  ) : (
    <>
      <div className="relative inline-block">
        <img src={config.iconSrc} alt={config.ariaLabel} />
        {config.id === "cart" && (
          <span className="absolute -top-4 -right-3 flex items-center justify-center h-5 w-5 rounded-full bg-[var(--color-green-100)] text-black text-xs font-bold">
            {shouldShowCartLoading ? (
              <div className="flex mx-auto">
                <LoadingSpinner size="sm" className="" />
              </div>
            ) : (
              config.badgeCount
            )}
          </span>
        )}
      </div>
    </>
  );

  const commonEvents = {
    onMouseEnter: handleShowTooltip,
    onMouseLeave: handleHideTooltip,
    onFocus: handleShowTooltip,
    onBlur: handleHideTooltip,
  };

  const wrapperProps = {
    className: `hover:bg-[rgba(52,57,70,.5)] cursor-pointer bg-[rgba(52,57,70,0.25)] rounded-full w-12 h-12 flex items-center justify-center ${className}`,
    onClick: config.onClick,
    style: config.onClick ? { cursor: "pointer" } : undefined,
    role: "button",
    tabIndex: 0,
    ...commonEvents,
  };

  return (
    <>
      {config.dataToggle ? (
        <a
          data-bs-toggle={config.dataToggle}
          data-bs-target={config.dataTarget}
          aria-controls={config.ariaControls}
          {...commonEvents}
          onClick={config.onClick}
        >
          <span {...wrapperProps}>{iconContent}</span>
        </a>
      ) : (
        <span {...wrapperProps}>{iconContent}</span>
      )}
      {isHovered && tooltipPos && (
        <Tooltip text={config.ariaLabel} position={tooltipPos} />
      )}
    </>
  );
};

export default IconButton;